import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './InfosStep.styles';
import {
  Button,
  IconAiStar,
  IconArrow,
  IconChevron,
  IconInfo, TextArea, TextField, Tooltip,
} from 'gatorcomponents';

import {
  isBRServer, isMXServer, validateEmail, validatePhoneBR, validatePhone,
} from '@/utils/Validators/validation';
import { COUNTRY as CONFIG_COUNTRY } from '@/config';
import { BRANDS_LIST } from '../../../AiBuilderCreateSite/AiBuilderCreateSite.enum';
import { useDispatch, useSelector } from 'react-redux';
import { agentsFactoryActions } from '@/redux/modules';

const InfosStep = ({
  continueButtonClick,
  infosStepLocales,
  userCountry,
  fieldDescriptionValue,
  setFieldDescriptionValue,
  fieldTelephoneValue,
  setFieldTelephoneValue,
  fieldEmailValue,
  setFieldEmailValue,
  toneOfVoiceValue,
  setToneOfVoiceValue,
  mobile,
}) => {
  const dispatch = useDispatch();

  const { improvedText, loadingImprovedText } = useSelector(state => state.agentsFactory);

  const [error, setError] = useState({
    email: '',
    telephone: '',
  });
  const [showDescriptionExample, setShowDescriptionExample] = useState(false);


  const disabledButton = !fieldDescriptionValue || error.email !== '' || error.telephone !== '' || !toneOfVoiceValue;

  const getPhoneCountry = useCallback(() => {
    if (BRANDS_LIST.includes(userCountry.toLowerCase())) {
      return userCountry.toLowerCase();
    }

    return CONFIG_COUNTRY;
  }, [userCountry]);

  const phoneCountry = getPhoneCountry();

  const isValidPhone = useCallback(() => {
    if (isBRServer) {
      return validatePhoneBR(fieldTelephoneValue);
    }

    if (isMXServer) {
      if (BRANDS_LIST.includes(userCountry.toLowerCase())) {
        return validatePhone(fieldTelephoneValue, userCountry.toLowerCase());
      }
      return fieldTelephoneValue.length > 6;
    }

    return validatePhone(fieldTelephoneValue);
  }, [fieldTelephoneValue, userCountry]);

  const validateErrors = useCallback(() => {
    const newErrors = { email: '', telephone: '' };

    if (fieldEmailValue.length > 0 && validateEmail(fieldEmailValue) !== true) {
      newErrors.email = infosStepLocales.errors.emailError;
    }

    if (fieldTelephoneValue.length > 0 && !isValidPhone()) {
      newErrors.telephone = infosStepLocales.errors.telephoneError;
    }

    setError(newErrors);
  }, [
    fieldTelephoneValue,
    fieldEmailValue,
    infosStepLocales.errors,
    isValidPhone,
  ]);

  const handleValidateErrors = useCallback(() => {
    validateErrors();
  }, [validateErrors]);

  useEffect(() => {
    handleValidateErrors();
  }, [handleValidateErrors]);

  const handleDescriptionChange = (e) => {
    setFieldDescriptionValue && setFieldDescriptionValue(e.target.value);
  };

  const handleTelephoneChange = (e) => {
    setFieldTelephoneValue && setFieldTelephoneValue(e.target.value);
  };

  const handleEmailChange = (e) => {
    setFieldEmailValue && setFieldEmailValue(e.target.value);
  };

  const handleToneOfVoiceChange = (value) => {
    setToneOfVoiceValue && setToneOfVoiceValue(value);
  };

  const handleImproveText = () => {
    dispatch(agentsFactoryActions.improveTextWithAI.request({
      prompt: fieldDescriptionValue,
    }));
  };

  useEffect(() => {
    if (improvedText) {
      setFieldDescriptionValue(improvedText);
      dispatch(agentsFactoryActions.improveTextWithAI.setImprovedText(null));
    }
  }, [improvedText, setFieldDescriptionValue, dispatch]);

  return (
    <Styles.Wrapper data-testid="infos-step-wrapper">
      <Styles.Title>{infosStepLocales.title}</Styles.Title>

      <Styles.WrapperForm>
        <Styles.Row>
          <Styles.Label>{infosStepLocales.inputDescriptionHeadText}</Styles.Label>
          <Styles.WrapperInput>
            <TextArea
              testId="infos-step-description-field"
              label={infosStepLocales.inputDescriptionPlaceholder}
              rows={4}
              onChange={handleDescriptionChange}
              value={loadingImprovedText ? '...' : fieldDescriptionValue}
              maxLength={300}
              remainingCharactersLabel={infosStepLocales.textRemainingCharactersLabel}
              remainingCharactersPosition="outside"
            />

            <Styles.ButtonImproveDescription>
              <Styles.ButtonText>
                <Button
                  label={infosStepLocales.buttonImproveDescription}
                  variant="text"
                  iconLeft={<IconAiStar color="primaryMedium" />}
                  onClick={handleImproveText}
                  size="small"
                  version="v2"
                  disabled={loadingImprovedText}
                />
              </Styles.ButtonText>
            </Styles.ButtonImproveDescription>
          </Styles.WrapperInput>


          <Styles.WrapperDescriptionExample>
            <Styles.ButtonText>
              <Button
                label={infosStepLocales.buttonDescriptionExample}
                variant="text"
                iconRight={<IconChevron direction={showDescriptionExample ? 'top' : 'bottom'} color="primaryMedium" />}
                onClick={() => setShowDescriptionExample(!showDescriptionExample)}
                size="small"
                version="v2"
              />
            </Styles.ButtonText>

            {showDescriptionExample && (
            <Styles.DescriptionExampleContent>
              {infosStepLocales.descriptionExample}
            </Styles.DescriptionExampleContent>
            )}
          </Styles.WrapperDescriptionExample>
        </Styles.Row>

        <Styles.Row>
          <Styles.Label>
            {infosStepLocales.contactInfos}
            <Styles.SmallText>{infosStepLocales.optional}</Styles.SmallText>
          </Styles.Label>
          <Styles.LabelDescription>{infosStepLocales.contactInfosDescription}</Styles.LabelDescription>

          <Styles.InputsListContainer>
            <Styles.InputContainerPhone>
              <TextField
                testId="infos-step-telephone-field"
                label={infosStepLocales.telephonePlaceholder}
                mask="phone"
                country={phoneCountry}
                value={fieldTelephoneValue}
                onChange={handleTelephoneChange}
                status={error.telephone !== '' ? 'attention' : 'normal'}
                helpText={error.telephone !== '' && error.telephone}
              />
            </Styles.InputContainerPhone>

            <Styles.InputContainerEmail>
              <TextField
                testId="infos-step-email-field"
                label={infosStepLocales.emailPlaceholder}
                value={fieldEmailValue}
                onChange={handleEmailChange}
                maxLength={90}
                status={error.email !== '' ? 'attention' : 'normal'}
                helpText={error.email !== '' && error.email}
              />
            </Styles.InputContainerEmail>
          </Styles.InputsListContainer>
        </Styles.Row>

        <Styles.Row>
          <Styles.Label>
            {infosStepLocales.toneOfVoiceLabel}
            <Tooltip content={infosStepLocales.toneOfVoiceTooltip} position="top" width={280} allowDynamicFit>
              <IconInfo color="primaryMedium" size="sm" />
            </Tooltip>
          </Styles.Label>
          <Styles.LabelDescription>{infosStepLocales.toneOfVoiceDescription}</Styles.LabelDescription>

          <Styles.ToneOfVoiceContainer>
            {infosStepLocales.toneOfVoiceOptionsList.map(option => (
              <Styles.ToneOfVoiceOption key={option.value} onClick={() => handleToneOfVoiceChange(option.value)} active={toneOfVoiceValue === option.value}>
                {option.icon(toneOfVoiceValue === option.value ? 'primaryMedium' : 'lowLight')}
                <Styles.ToneOfVoiceOptionLabel active={toneOfVoiceValue === option.value}>
                  {option.label}
                </Styles.ToneOfVoiceOptionLabel>
              </Styles.ToneOfVoiceOption>
            ))}
          </Styles.ToneOfVoiceContainer>
        </Styles.Row>
      </Styles.WrapperForm>

      <Styles.ButtonWrapper>
        <Button
          testId="infos-step-continue-button"
          label={infosStepLocales.buttonLabel}
          width={mobile ? 'full' : 'fit'}
          size="large"
          onClick={continueButtonClick}
          disabled={disabledButton}
          iconRight={
            <IconArrow direction="right" />
          }
        />
      </Styles.ButtonWrapper>
    </Styles.Wrapper>
  );
};

export default InfosStep;
